export type AcceptanceLetterTemplateVariant =
  | 'initial-all'
  | 'complete-boys'
  | 'complete-girls';

export type AcceptanceLetterContext = {
  currentDate: string;
  familyName: string;
  childName: string;
  childFirstName: string;
  registrationFeeDescription: string;
  schoolYear: string;
  schoolName: string;
  schoolMission: string;
  schoolAddress: string;
  schoolPhone: string;
  schoolEmail: string;
  schoolAdmissionsSignerName?: string;
  schoolAdmissionsSignerTitle?: string;
  schoolDivisionName?: string;
  childGenderNoun: 'son' | 'daughter' | 'child';
  childPronounSubject: 'he' | 'she' | 'they';
  childPronounPossessive: 'his' | 'her' | 'their';
};

type AcceptanceLetterTemplateDefinition = {
  label: string;
  fileStem: string;
  body: string;
};

const TEMPLATE_MAP: Record<AcceptanceLetterTemplateVariant, AcceptanceLetterTemplateDefinition> = {
  'initial-all': {
    label: 'Initial Acceptance Letter (All)',
    fileStem: 'initial-acceptance-letter',
    body: `
{{currentDate}}

Dear {{familyName}} Family,

We have received and processed your application for {{childName}} and are pleased to inform you that {{childPronounSubject}} has been accepted to {{schoolName}} beginning with the {{schoolYear}} school year.

For the admission process to be complete, please complete the enrollment and registration form in your online portal within two weeks of receiving this acceptance and submit to the business office along with the non-refundable registration fee of {{registrationFeeDescription}}.
You can find your tuition contract in the online portal.

If you have any questions, please feel free to reach out to us.

Sincerely,
{{schoolAdmissionsSignerName}}
{{schoolAdmissionsSignerTitle}}
{{schoolDivisionName}}
`.trim(),
  },
  'complete-boys': {
    label: 'Complete Acceptance Letter (Boys)',
    fileStem: 'complete-acceptance-letter-boys',
    body: `
{{currentDate}}

Dear {{familyName}} Family,

We are pleased to inform you that {{childFirstName}} has been accepted to {{schoolName}} beginning with the {{schoolYear}} school year.

{{schoolMission}}
It is our goal to provide {{childFirstName}} with this formative education for the duration of {{childPronounPossessive}} elementary school years.

Again, welcome to {{schoolName}}. We look forward to partnering with you in the chinuch of your {{childGenderNoun}}.

Sincerely,
{{schoolAdmissionsSignerName}}
{{schoolAdmissionsSignerTitle}}
{{schoolDivisionName}}
`.trim(),
  },
  'complete-girls': {
    label: 'Complete Acceptance Letter (Girls)',
    fileStem: 'complete-acceptance-letter-girls',
    body: `
{{currentDate}}

Dear {{familyName}} Family,

We have received and processed your application for {{childFirstName}} and are pleased to inform you that {{childPronounSubject}} has been accepted to {{schoolName}} beginning with the {{schoolYear}} school year.

{{schoolMission}}
It is our goal to provide {{childFirstName}} with this formative education for the duration of {{childPronounPossessive}} elementary school years.

Again, welcome to {{schoolName}}. We look forward to partnering with you in the chinuch of your {{childGenderNoun}}.

Sincerely,
{{schoolAdmissionsSignerName}}
{{schoolAdmissionsSignerTitle}}
{{schoolDivisionName}}
`.trim(),
  },
};

const normalizeGender = (value: unknown): string => String(value ?? '').trim().toLowerCase();
const normalizeDivision = (value: unknown): string => String(value ?? '').trim().toLowerCase();
const normalizeStatus = (value: unknown): string => String(value ?? '').trim().toLowerCase();
const readText = (value: unknown, fallback = ''): string => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text || fallback;
};

const isYstnjSchoolName = (schoolName: string): boolean => {
  const normalized = schoolName.toLowerCase();
  return normalized.includes('yeshiva shaarei tzion') || normalized.includes('ystnj');
};

const neutralSignerFallbackByVariant: Record<AcceptanceLetterTemplateVariant, {
  name: string;
  title: string;
  divisionName: string;
}> = {
  'initial-all': {
    name: '[Admissions signer name]',
    title: '[Admissions signer title]',
    divisionName: '[School or admissions office name]',
  },
  'complete-boys': {
    name: '[Admissions signer name]',
    title: '[Admissions signer title]',
    divisionName: '[Division or school name]',
  },
  'complete-girls': {
    name: '[Admissions signer name]',
    title: '[Admissions signer title]',
    divisionName: '[Division or school name]',
  },
};

const ystnjSignerFallbackByVariant: Record<AcceptanceLetterTemplateVariant, {
  name: string;
  title: string;
  divisionName: string;
}> = {
  'initial-all': {
    name: 'Rabbi E Malitzky',
    title: '',
    divisionName: '',
  },
  'complete-boys': {
    name: 'Rabbi Shragi Herskowitz',
    title: 'Menahel',
    divisionName: 'Yeshiva Shaarei Tzion Boys School',
  },
  'complete-girls': {
    name: 'Mrs. Shoshana Sauer',
    title: 'Principal',
    divisionName: 'Yeshiva Shaarei Tzion Girls School',
  },
};

const isMaleGender = (value: unknown): boolean => {
  const normalized = normalizeGender(value);
  return normalized === 'm' || normalized.startsWith('male') || normalized.startsWith('boy');
};

const isFemaleGender = (value: unknown): boolean => {
  const normalized = normalizeGender(value);
  return normalized === 'f' || normalized.startsWith('female') || normalized.startsWith('girl');
};

const toReplacementMap = (
  context: AcceptanceLetterContext,
  variant: AcceptanceLetterTemplateVariant,
): Record<string, string> => {
  const signerFallback = isYstnjSchoolName(context.schoolName)
    ? ystnjSignerFallbackByVariant[variant]
    : neutralSignerFallbackByVariant[variant];
  return {
    currentDate: context.currentDate,
    familyName: context.familyName,
    childName: context.childName,
    childFirstName: context.childFirstName,
    registrationFeeDescription: context.registrationFeeDescription,
    schoolYear: context.schoolYear,
    schoolName: context.schoolName,
    schoolMission: context.schoolMission,
    schoolAddress: context.schoolAddress,
    schoolPhone: context.schoolPhone,
    schoolEmail: context.schoolEmail,
    schoolAdmissionsSignerName: readText(context.schoolAdmissionsSignerName, signerFallback.name),
    schoolAdmissionsSignerTitle: readText(context.schoolAdmissionsSignerTitle, signerFallback.title),
    schoolDivisionName: readText(context.schoolDivisionName, signerFallback.divisionName),
    childGenderNoun: context.childGenderNoun,
    childPronounSubject: context.childPronounSubject,
    childPronounPossessive: context.childPronounPossessive,
  };
};

const renderTemplateBody = (templateBody: string, replacements: Record<string, string>): string =>
  templateBody.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_match, token: string) => replacements[token] ?? '');

export const resolveChildGenderWords = (
  gender: unknown,
): Pick<AcceptanceLetterContext, 'childGenderNoun' | 'childPronounSubject' | 'childPronounPossessive'> => {
  if (isMaleGender(gender)) {
    return { childGenderNoun: 'son', childPronounSubject: 'he', childPronounPossessive: 'his' };
  }
  if (isFemaleGender(gender)) {
    return { childGenderNoun: 'daughter', childPronounSubject: 'she', childPronounPossessive: 'her' };
  }
  return { childGenderNoun: 'child', childPronounSubject: 'they', childPronounPossessive: 'their' };
};

export const resolveAcceptanceLetterTemplateVariant = (input: {
  childStatus?: unknown;
  division?: unknown;
  gender?: unknown;
}): AcceptanceLetterTemplateVariant => {
  const status = normalizeStatus(input.childStatus);
  const division = normalizeDivision(input.division);

  const isCompleteVariant = (
    status === 'accepted'
    || status === 'accepted_pending_enrollment'
    || status === 'enrolled'
    || status === 'accepted_complete'
    || status === 'accepted_complete_pending_enrollment'
  );

  if (!isCompleteVariant) {
    return 'initial-all';
  }

  if (division.includes('boys') || isMaleGender(input.gender)) {
    return 'complete-boys';
  }
  return 'complete-girls';
};

export const renderAcceptanceLetter = (
  variant: AcceptanceLetterTemplateVariant,
  context: AcceptanceLetterContext,
): { fileStem: string; label: string; content: string } => {
  const definition = TEMPLATE_MAP[variant];
  const replacements = toReplacementMap(context, variant);
  const content = renderTemplateBody(definition.body, replacements).trim();
  return {
    fileStem: definition.fileStem,
    label: definition.label,
    content: `${content}\n`,
  };
};
